import { Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useAtomValue } from 'jotai';
import { ConfirmDeletionDialog } from '../../../../../components/Dialog/ConfirmDialog/ConfirmDeletionDialog';
import { KPIRequestFrequency, KPIRequestStatus } from '../../../../../data-models/kpi-requests.data-model';
import { useKPIRequestsNotSentActions } from '../hooks/useKPIRequestsNotSentActions';
import { useRequestActions } from '../KPIRequestResponse/hooks/useKPIRequestResponseActions';
import { KpiAction } from '../../../../../services/state/KPI/KPIRequestsStateJ';
import { companyStateJ } from '../../../../../services/state/CompanyStateJ';

export function ConfirmDeleteRequest() {
  const { deleteKPIRequestAction } = useKPIRequestsNotSentActions();
  const [loading, setLoading] = useState(false);
  const { modalAction, selectedRequest, onCloseModal } = useRequestActions();
  const company = useAtomValue(companyStateJ(selectedRequest?.companyId ?? -1));

  const handleDelete = useCallback(async () => {
    if (!selectedRequest) {
      return;
    }
    setLoading(true);
    await deleteKPIRequestAction(selectedRequest.id);
    setLoading(false);
    onCloseModal();
  }, [deleteKPIRequestAction, onCloseModal, selectedRequest]);

  if (modalAction !== KpiAction.archive || !selectedRequest) {
    return null;
  }

  return (
    <ConfirmDeletionDialog
      open
      onClose={onCloseModal}
      onConfirm={handleDelete}
      title={`Delete ${RequestFrequencyDict[selectedRequest.frequency]} request for ${company?.name}?`}
      loading={loading}
    >
      <Typography variant='body2' color={'text.secondary'}>
        {getDeleteRequestMessage(selectedRequest.status)}
      </Typography>
    </ConfirmDeletionDialog>
  );
}

export const RequestFrequencyDict: Record<KPIRequestFrequency, string> = {
  [KPIRequestFrequency.Monthly]: 'monthly',
  [KPIRequestFrequency.Quarterly]: 'quarterly',
  [KPIRequestFrequency.Annual]: 'annual',
  [KPIRequestFrequency.None]: '',
};

const RESPONDED_STATUSES = new Set<KPIRequestStatus>([
  KPIRequestStatus.Submitted,
  KPIRequestStatus.Accepted,
  KPIRequestStatus.Rejected,
  KPIRequestStatus.ManagedServiceSubmitted,
  KPIRequestStatus.ManagedServiceInProgress,
  KPIRequestStatus.ManagedServiceFinished,
]);

function getDeleteRequestMessage(status: KPIRequestStatus) {
  if (status === KPIRequestStatus.NotSent) {
    return 'This action will skip the request for the selected period.';
  }
  if (RESPONDED_STATUSES.has(status)) {
    return 'The respondent will not be able to make further changes. No data is permanently deleted — the request will move to the Deleted tab.';
  }
  return 'The respondent will not be able to complete the deleted request';
}
